import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Form as AntdForm } from 'antd';
import dayjs from 'dayjs';
import { required } from '../../../../antd/form/FormValidation';
import PhdFormBuilder from '../PhdFormBuilder';
import PhdHelpHint from '../../../../common/Phd/PhdHelpHint.jsx';
import {
  STUDENT_STATE_OPTION_KEYS,
  STUDY_FORM_OPTION_KEYS,
  translateOptions,
} from '../phdFormOptions.jsx';

const PhdStudentsEditForm = ({
  personOptions,
  departmentOptions,
  studyFieldOptions,
  isSubmitting,
  isPhdAdmin,
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  const form = AntdForm.useFormInstance();

  const studyFormOptions = useMemo(
    () => translateOptions(STUDY_FORM_OPTION_KEYS, t),
    [t],
  );
  const studentStateOptions = useMemo(
    () =>
      translateOptions(STUDENT_STATE_OPTION_KEYS, t).map((option) => ({
        ...option,
        disabled:
          option.value === 'Interrupted' ||
          (option.value === 'Active' && !isPhdAdmin),
      })),
    [isPhdAdmin, t],
  );
  const selectedStudyForm = AntdForm.useWatch('study_form', form);
  const durationOptions = useMemo(
    () =>
      selectedStudyForm === 'External'
        ? [
            { value: 48, label: t('48 mesiacov') },
            { value: 60, label: t('60 mesiacov') },
          ]
        : [
            { value: 36, label: t('36 mesiacov') },
            { value: 48, label: t('48 mesiacov') },
          ],
    [selectedStudyForm, t],
  );
  const activeRequiredRule = useMemo(
    () => ({
      validator: (_, value) => {
        if (form.getFieldValue('state') !== 'Active' || value) {
          return Promise.resolve();
        }

        return Promise.reject(t('Aktívny doktorand vyžaduje kompletný profil'));
      },
    }),
    [form, t],
  );
  const phoneRule = useMemo(
    () => ({
      validator: (_, value) => {
        if (
          value === undefined ||
          value === null ||
          String(value).trim() === ''
        ) {
          return Promise.resolve();
        }

        return /^(?:\+?[0-9]{1,3}[\s-]?)?(?:[0-9][\s-]?){6,14}[0-9]$/.test(
          String(value).trim(),
        )
          ? Promise.resolve()
          : Promise.reject(t('Pole obsahuje neplatné telefónne číslo'));
      },
    }),
    [t],
  );
  const addressTextRule = useMemo(
    () => ({
      validator: (_, value) => {
        if (
          value === undefined ||
          value === null ||
          String(value).trim() === ''
        ) {
          return Promise.resolve();
        }

        const normalized = String(value).trim();
        if (normalized.length < 2) {
          return Promise.reject(t('Pole musí obsahovať aspoň 2 znaky'));
        }

        return /^[\p{L}0-9\s.,'/-]+$/u.test(normalized)
          ? Promise.resolve()
          : Promise.reject(t('Pole obsahuje neplatné znaky'));
      },
    }),
    [t],
  );
  const postalCodeRule = useMemo(
    () => ({
      validator: (_, value) => {
        if (
          value === undefined ||
          value === null ||
          String(value).trim() === ''
        ) {
          return Promise.resolve();
        }

        const normalized = String(value).trim();
        if (normalized.length < 2) {
          return Promise.reject(t('Pole musí obsahovať aspoň 2 znaky'));
        }

        return /^[0-9A-Za-z\s-]+$/.test(normalized)
          ? Promise.resolve()
          : Promise.reject(t('Pole obsahuje neplatné PSČ'));
      },
    }),
    [t],
  );
  const houseNumberRule = useMemo(
    () => ({
      validator: (_, value) => {
        if (
          value === undefined ||
          value === null ||
          String(value).trim() === ''
        ) {
          return Promise.resolve();
        }

        return /^[0-9A-Za-z/-\s]+$/.test(String(value).trim())
          ? Promise.resolve()
          : Promise.reject(t('Pole obsahuje neplatné súpisné číslo'));
      },
    }),
    [t],
  );
  const birthDateRule = useMemo(
    () => ({
      validator: (_, value) => {
        if (!value) {
          return Promise.resolve();
        }

        return value.isAfter(dayjs(), 'day')
          ? Promise.reject(t('Dátum narodenia musí byť v minulosti'))
          : Promise.resolve();
      },
    }),
    [t],
  );
  const sections = useMemo(
    () => [
      {
        key: 'basic',
        title: t('Základné údaje'),
        fields: [
          {
            name: 'person_id',
            label: t('Študent'),
            type: 'select',
            options: personOptions,
            rules: [required(t)],
            colSpan: 12,
          },
          {
            name: 'department_id',
            label: t('Katedra'),
            type: 'select',
            options: departmentOptions,
            rules: [activeRequiredRule],
            colSpan: 12,
          },
          {
            name: 'phd_study_field_id',
            label: t('Študijný odbor'),
            type: 'select',
            options: studyFieldOptions,
            rules: [activeRequiredRule],
            colSpan: 12,
          },
          {
            name: 'study_form',
            label: (
              <span>
                {t('Forma štúdia')}
                <PhdHelpHint
                  label={t('Čo znamená forma štúdia?')}
                  title={t(
                    'Denná alebo externá forma určuje štandardnú dĺžku štúdia a odporúčaný aj maximálny termín dizertačnej skúšky.',
                  )}
                />
              </span>
            ),
            type: 'select',
            options: studyFormOptions,
            rules: [activeRequiredRule],
            colSpan: 12,
          },
          {
            name: 'standard_duration_months',
            label: t('Štandardná dĺžka štúdia'),
            type: 'select',
            options: durationOptions,
            rules: [activeRequiredRule],
            colSpan: 12,
          },
          {
            name: 'state',
            label: t('Stav štúdia'),
            type: 'select',
            options: studentStateOptions,
            colSpan: 12,
          },
          {
            name: 'birthplace',
            label: t('Miesto narodenia'),
            type: 'text',
            rules: [activeRequiredRule],
            colSpan: 12,
          },
          {
            name: 'birth_date',
            label: t('Dátum narodenia'),
            type: 'date',
            componentProps: { style: { width: '100%' } },
            rules: [activeRequiredRule, birthDateRule],
            colSpan: 12,
          },
          {
            name: 'phone',
            label: t('Telefón'),
            type: 'text',
            rules: [phoneRule],
            colSpan: 12,
          },
          {
            name: 'date_study_start',
            label: t('Začiatok štúdia'),
            type: 'date',
            componentProps: { style: { width: '100%' } },
            rules: [activeRequiredRule],
            colSpan: 12,
          },
        ],
      },
      {
        key: 'address',
        title: t('Adresa'),
        fields: [
          {
            name: 'address_street',
            label: t('Ulica'),
            type: 'text',
            rules: [activeRequiredRule, addressTextRule],
            colSpan: 12,
          },
          {
            name: 'address_house_number',
            label: t('Súpisné číslo'),
            type: 'text',
            rules: [activeRequiredRule, houseNumberRule],
            colSpan: 12,
          },
          {
            name: 'address_city',
            label: t('Mesto'),
            type: 'text',
            rules: [activeRequiredRule, addressTextRule],
            colSpan: 12,
          },
          {
            name: 'address_postal_code',
            label: t('PSČ (ZIP)'),
            type: 'text',
            rules: [activeRequiredRule, postalCodeRule],
            colSpan: 12,
          },
          {
            name: 'address_country',
            label: t('Krajina'),
            type: 'text',
            rules: [activeRequiredRule, addressTextRule],
            colSpan: 12,
          },
        ],
      },
      {
        key: 'exam',
        title: t('Dizertačná skúška'),
        fields: [
          {
            name: 'application_submission_year',
            label: t('Rok podania'),
            type: 'number',
            componentProps: { min: 1990, max: 2099, style: { width: '100%' } },
            colSpan: 12,
          },
          {
            name: 'date_dissertation_exam',
            label: t('Dátum skúšky'),
            type: 'date',
            componentProps: { style: { width: '100%' } },
            colSpan: 12,
          },
          {
            name: 'dissertation_exam_result',
            label: t('Výsledok'),
            type: 'select',
            options: ['A', 'B', 'C', 'D', 'E', 'Fx'].map((grade) => ({
              value: grade,
              label: grade,
            })),
            componentProps: { allowClear: true },
            colSpan: 12,
          },
          {
            name: 'dissertation_exam_transcript',
            label: t('Protokol'),
            type: 'text',
            colSpan: 12,
          },
        ],
      },
    ],
    [
      t,
      personOptions,
      departmentOptions,
      studyFieldOptions,
      studyFormOptions,
      durationOptions,
      studentStateOptions,
      activeRequiredRule,
      phoneRule,
      addressTextRule,
      postalCodeRule,
      houseNumberRule,
      birthDateRule,
    ],
  );

  return (
    <PhdFormBuilder
      sections={sections}
      submitKey="phd-students"
      isSubmitting={isSubmitting}
      hideSubmit={isReadOnly}
    />
  );
};

export default PhdStudentsEditForm;
